import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { formatDateForAPI } from "../../service/subscriptionSchedule";
import {
  getRestaurantLatitude,
  getRestaurantLongitude,
} from "../../utils/JwtUtil";

// Component imports
import MapHeader from "./map/MapHeader";
import MapLegend from "./map/MapLegend";
import GoogleMapContainer from "./map/GoogleMapContainer";
import ScheduleDetailsModal from "./map/ScheduleDetailsModal";

// Hook imports
import { useMapData } from "./map/hooks/useMapData";
import { useScheduleModal } from "./map/hooks/useScheduleModal";
import { useGoogleMaps } from "./map/hooks/useGoogleMaps";

// Utility imports
import { getStatusColor, getStatusText } from "./map/utils/mapUtils";

// Google Maps API Key
const GOOGLE_MAPS_API_KEY =
  process.env.REACT_APP_GOOGLE_MAPS_API_KEY ||
  "AIzaSyDV-3Clu3ar3LDDNU7Wcq9uWZLIZKBxtw8";

/**
 * Subscription Schedule Map Component
 * Shows Google Maps with pins for scheduled deliveries
 */
const SubscriptionScheduleMap = ({
  restaurantId,
  selectedDate = null,
  language = "en",
}) => {
  const { t } = useTranslation();

  // Get restaurant coordinates from JWT token
  const restaurantLatitude = getRestaurantLatitude();
  const restaurantLongitude = getRestaurantLongitude();

  // Default map center (use restaurant location if available, otherwise Amman, Jordan)
  const defaultCenter = useMemo(() => {
    if (restaurantLatitude && restaurantLongitude) {
      return {
        lat: parseFloat(restaurantLatitude),
        lng: parseFloat(restaurantLongitude),
      };
    }
    return { lat: 31.9454, lng: 35.9284 }; // Amman, Jordan fallback
  }, [restaurantLatitude, restaurantLongitude]);

  const defaultZoom = 12;

  // Custom hooks
  const {
    schedules,
    loading,
    error,
    selectedTimeFilter,
    setSelectedTimeFilter,
    getUniqueTimeRanges,
    filteredSchedules,
    loadSchedules,
  } = useMapData(restaurantId, selectedDate, language);

  const {
    selectedSchedule,
    showInfoWindow,
    copyButtonText,
    isCopying,
    handleMarkerClick,
    closeInfoWindow,
    copyLocationLink,
  } = useScheduleModal();

  const { setMapInstance, setMapsInstance } = useGoogleMaps(schedules);

  // Status utility functions with translation
  const getStatusColorWithTranslation = (status) => getStatusColor(status);
  const getStatusTextWithTranslation = (status) => getStatusText(status, t);

  // Handle Google Maps API load
  const handleGoogleApiLoaded = ({ map, maps }) => {
    console.log("Google Maps API loaded successfully");
    setMapInstance(map);
    setMapsInstance(maps);

    // Initial bounds fitting if schedules are already loaded
    if (schedules.length > 0) {
      const bounds = new maps.LatLngBounds();
      let hasValidLocation = false;

      schedules.forEach((schedule) => {
        if (schedule.location?.latitude && schedule.location?.longitude) {
          const lat = parseFloat(schedule.location.latitude);
          const lng = parseFloat(schedule.location.longitude);

          if (!isNaN(lat) && !isNaN(lng)) {
            bounds.extend({ lat, lng });
            hasValidLocation = true;
          }
        }
      });

      if (hasValidLocation) {
        map.fitBounds(bounds);

        // Prevent over-zooming for single marker
        if (schedules.length === 1) {
          const listener = maps.event.addListener(map, "idle", () => {
            if (map.getZoom() > 16) map.setZoom(16);
            maps.event.removeListener(listener);
          });
        }
      }
    }
  };

  // Handle Google Maps error
  const handleMapError = (error) => {
    console.error("Google Maps Error:", error);
    // Don't set error state here to avoid infinite re-renders
  };

  // API Key validation
  if (
    !GOOGLE_MAPS_API_KEY ||
    GOOGLE_MAPS_API_KEY === "YOUR_GOOGLE_MAPS_API_KEY"
  ) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg
              className="h-5 w-5 text-yellow-400"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-yellow-800">
              {t("scheduleMap.googleMapsNotLoaded")}
            </h3>
            <div className="mt-1 text-sm text-yellow-700">
              {t("scheduleMap.googleMapsRequired")}
            </div>
            <div className="mt-2 text-xs text-yellow-600">
              API Key:{" "}
              {GOOGLE_MAPS_API_KEY
                ? `...${GOOGLE_MAPS_API_KEY.slice(-8)}`
                : "Not found"}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow">
      {/* Map Header */}
      <MapHeader
        t={t}
        selectedDate={selectedDate}
        formatDateForAPI={formatDateForAPI}
        filteredSchedules={filteredSchedules}
        selectedTimeFilter={selectedTimeFilter}
        setSelectedTimeFilter={setSelectedTimeFilter}
        getUniqueTimeRanges={getUniqueTimeRanges}
        loading={loading}
        loadSchedules={loadSchedules}
      />

      {/* Legend */}
      <MapLegend
        t={t}
        getStatusColor={getStatusColorWithTranslation}
        getStatusText={getStatusTextWithTranslation}
      />

      {/* Error Message */}
      {error && (
        <div className="px-6 py-3 bg-red-50 border-b border-red-200">
          <div className="text-sm text-red-700">
            {t("scheduleMap.error")}: {error}
          </div>
        </div>
      )}

      {/* Map Container */}
      <GoogleMapContainer
        GOOGLE_MAPS_API_KEY={GOOGLE_MAPS_API_KEY}
        defaultCenter={defaultCenter}
        defaultZoom={defaultZoom}
        schedules={schedules}
        onGoogleApiLoaded={handleGoogleApiLoaded}
        onError={handleMapError}
        filteredSchedules={filteredSchedules}
        handleMarkerClick={handleMarkerClick}
        getStatusColor={getStatusColorWithTranslation}
        getStatusText={getStatusTextWithTranslation}
        restaurantLatitude={restaurantLatitude}
        restaurantLongitude={restaurantLongitude}
        t={t}
        loading={loading}
      />

      {/* Schedule Details Modal */}
      <ScheduleDetailsModal
        showInfoWindow={showInfoWindow}
        selectedSchedule={selectedSchedule}
        closeInfoWindow={closeInfoWindow}
        t={t}
        getStatusColor={getStatusColorWithTranslation}
        getStatusText={getStatusTextWithTranslation}
        copyLocationLink={copyLocationLink}
        copyButtonText={copyButtonText}
        isCopying={isCopying}
      />
    </div>
  );
};

export default SubscriptionScheduleMap;
